import { AfterViewInit, Component, EventEmitter, Output, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { GlobalSearchService } from '../../../../../../services/global-search.service';
import { GlobalSearchResultDto } from '../../../../../../models/api/globalSearchResultDto';
import { GlobalSearchRequest } from '../../../../../../models/api/globalSearchRequest';
import { EntityType } from '../../../../../../models/enums/entityType.enum';
import { Helpers } from '../../../../../../utilities/helpers';
import { TextNormalizeDirective } from '../../../../../../directives/text-normalize.directive';

@Component({
  selector: 'app-add-proposal-name-search',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatInputModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    NgxSpinnerModule,
    FormsModule,
    TextNormalizeDirective
  ],
  templateUrl: './add-proposal-name-search.component.html',
  styleUrls: ['./add-proposal-name-search.component.scss']
})
export class AddProposalNameSearchComponent implements AfterViewInit {
  @Output() proposalNameSelected = new EventEmitter<GlobalSearchResultDto | null>();

  private globalSearchService = inject(GlobalSearchService);
  private spinnerService = inject(NgxSpinnerService);
  private router = inject(Router);

  Helpers = Helpers;

  dataSource = new MatTableDataSource<GlobalSearchResultDto>([]);
  displayedColumns: string[] = ['actions', 'matchedYearsIcon', 'code', 'name', 'entityType'];
  pageSize: number = 5;
  selectedItem: GlobalSearchResultDto | null = null;
  searchTerm: string = '';

  @ViewChild(MatSort, { static: false }) sort!: MatSort;
  @ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  onSearch(): void {
    if (!this.searchTerm?.trim()) return;

    const request: GlobalSearchRequest = {
      searchTerm: this.searchTerm.trim(),
      pageNumber: 1,
      pageSize: 100,
      sortBy: 'Name',
      ascending: true,
      entityTypes: [EntityType.Director, EntityType.LargeShareholder, EntityType.VoteProposalName]
    };

    this.spinnerService.show();
    this.globalSearchService.search(request).subscribe({
      next: (response) => {
        this.dataSource.data = response.data.map(r => {
          if (r.matchedYears?.length && (r.year == null || !r.matchedYears.includes(r.year))) {
            return { ...r, matchedYearsTooltip: `Matched in ${r.matchedYears.join(', ')}` };
          }
          return r;
        });
        if (this.paginator) {
          this.paginator.firstPage();
        }
        this.selectedItem = null;
        this.proposalNameSelected.emit(null);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error searching:', error);
        this.spinnerService.hide();
      }
    });
  }

  onClear(): void {
    this.searchTerm = '';
    this.dataSource.data = [];
    this.selectedItem = null;
    this.proposalNameSelected.emit(null);
  }

  onConnect(item: GlobalSearchResultDto): void {
    if (this.selectedItem?.id === item.id && this.selectedItem?.entityType === item.entityType) {
      this.selectedItem = null;
    } else {
      this.selectedItem = item;
    }
    this.proposalNameSelected.emit(this.selectedItem);
  }

  isSelected(item: GlobalSearchResultDto): boolean {
    return this.selectedItem?.id === item.id && this.selectedItem?.entityType === item.entityType;
  }

  /**
   * Same aid as the Ownership tab's shareholder search: directors are the results most often confused
   * with one another — same or similar names, several companies each — so their record can be opened
   * alongside the dialog to check the CV before connecting.
   */
  canOpenInNewTab(item: GlobalSearchResultDto): boolean {
    return item.entityType === EntityType.Director;
  }

  onOpenInNewTab(item: GlobalSearchResultDto, event: MouseEvent): void {
    event.stopPropagation();
    if (!this.canOpenInNewTab(item)) return;

    const url = this.router.serializeUrl(this.router.createUrlTree(['/directors/edit', item.id]));
    window.open(url, '_blank');
  }
}
